/**
 * Exam Result — תוצאות המבחן
 * Score plus per-question feedback with the authored explanation, so a wrong answer
 * teaches something instead of just costing a point.
 */

import { Check, X, RotateCcw, Trophy } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ExamResult } from '../../types'

interface ExamResultViewProps {
  result: ExamResult
  onRetry: () => void
}

export default function ExamResultView({ result, onRetry }: ExamResultViewProps) {
  const canRetry = result.attemptsRemaining === null || result.attemptsRemaining > 0

  return (
    <div>
      <div
        role="status"
        className={`rounded-2xl border-2 p-6 text-center ${
          result.passed ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'
        }`}
      >
        <span
          className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${
            result.passed ? 'bg-green-200 text-green-900' : 'bg-amber-200 text-amber-900'
          }`}
        >
          {result.passed ? <Trophy size={26} aria-hidden="true" /> : <RotateCcw size={26} aria-hidden="true" />}
        </span>
        <p className="mt-3 text-3xl font-bold text-gray-900">
          <span dir="ltr">{result.scorePercent}%</span>
        </p>
        <p className="mt-1 text-sm text-gray-800">
          ענית נכון על {result.correctCount} מתוך {result.totalQuestions} שאלות
        </p>
        <p className="mt-2 text-base font-semibold text-gray-900">
          {result.passed
            ? 'כל הכבוד, עברת את המבחן המסכם!'
            : `לא נורא — ציון עובר הוא ${result.passScorePercent}%. אפשר לחזור על הנושאים ולנסות שוב.`}
        </p>
        {result.attemptsRemaining !== null && (
          <p className="mt-1 text-xs text-gray-700">נותרו {result.attemptsRemaining} ניסיונות</p>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {canRetry && (
          <button type="button" onClick={onRetry} className="btn btn-primary">
            <RotateCcw size={16} aria-hidden="true" className="ms-1" />
            ניסיון נוסף
          </button>
        )}
        <Link to="/learn" className="btn btn-secondary">
          חזרה למסלול הלמידה
        </Link>
      </div>

      <h2 className="mt-8 text-lg font-bold text-gray-900">פירוט התשובות</h2>
      <ol className="mt-4 space-y-4">
        {result.results.map((row, index) => (
          <li
            key={row.questionId}
            className={`rounded-2xl border p-5 ${
              row.correct ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
            }`}
          >
            <div className="flex items-start gap-2">
              <span
                className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                  row.correct ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'
                }`}
              >
                {row.correct ? <Check size={13} aria-hidden="true" /> : <X size={13} aria-hidden="true" />}
              </span>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {index + 1}. {row.questionText}
                </h3>
                {/* Status in words, not colour alone. */}
                <p className="mt-1 text-xs font-medium text-gray-700">
                  {row.correct ? 'תשובה נכונה' : 'תשובה שגויה'}
                </p>

                <ul className="mt-2 space-y-1">
                  {row.options.map((opt, optIndex) => {
                    const isCorrect = optIndex === row.correctIndex
                    const isChosen = optIndex === row.selectedIndex
                    return (
                      <li
                        key={optIndex}
                        className={`text-sm ${
                          isCorrect
                            ? 'font-semibold text-green-900'
                            : isChosen
                              ? 'text-red-900 line-through'
                              : 'text-gray-700'
                        }`}
                      >
                        {opt.text}
                        {isCorrect && <span className="ms-2 text-xs">(התשובה הנכונה)</span>}
                        {isChosen && !isCorrect && <span className="ms-2 text-xs">(בחרת)</span>}
                      </li>
                    )
                  })}
                </ul>

                {row.explanation && (
                  <p className="mt-3 rounded-lg bg-white/70 p-3 text-sm text-gray-800">
                    {row.explanation}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
